/**
 * SellerBot Conversation Service
 * 
 * Servicio especializado para extraer y gestionar conversaciones del SellerBot
 * desde archivos locales y WhatsApp Service como fallback.
 * 
 * ESTRATEGIA PRINCIPAL: Lectura directa de archivos ../WS/database/conversations/SB_*.json
 * ESTRATEGIA FALLBACK: Endpoint https://backend.anubisai.net/api/whatsapp/bot/conversations
 * 
 * @author ANUBIS AI Team
 * @version 3.0.0 - Con lectura directa de archivos optimizada
 */

import axios, { AxiosResponse } from 'axios';
import { getHatateToken } from '../helpers/utils';
import { SellerbotService } from '../service/sellerbotService';


// ==================== CONFIGURACIÓN ====================

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/bot';
const API_SEND_URL = 'https://backend.anubisai.net/api/whatsapp/send';
const API_KEY = 'anubis-whatsapp-api-key';

// ==================== INTERFACES ====================

export interface SellerConversation {
  id: string;                    // WhatsApp Chat ID (ej: "SB_56912345678@c.us")
  contactName: string;           // Nombre del contacto (extraído o formateado)
  contactPhone: string;          // Número de teléfono formateado
  contactEmail?: string;         // Email si está disponible
  lastMessageTime: string;       // Timestamp del último mensaje
  firstMessageTime: string;      // Timestamp del primer mensaje
  messageCount: number;          // Cantidad total de mensajes
  preview: string;               // Vista previa del último mensaje
  isActive: boolean;             // Si la conversación está activa (< 5 min)
  stage: 'prospecting' | 'interest' | 'negotiation' | 'closing' | 'closed' | 'lost'; // Etapa de venta
  value: number;                 // Valor estimado de la venta
  probability: number;           // Probabilidad de cierre (0-100)
  profileUsed: string;           // Perfil/personalidad utilizada
  productOffered: string;        // Producto ofrecido
  whatsappChatId: string;        // ID completo del chat de WhatsApp
  messages: SellerMessage[];     // Mensajes de la conversación
  source: string;                // Fuente/origen de la conversación
  notes?: string;                // Notas adicionales
}

export interface SellerMessage {
  id: string;                    // ID único del mensaje
  timestamp: string;             // Timestamp del mensaje
  sender: 'bot' | 'client' | string; // Remitente del mensaje
  text: string;                  // Contenido del mensaje
  type: 'text' | 'image' | 'document'; // Tipo de mensaje
  messageId?: string;            // ID del mensaje en WhatsApp
  status?: 'sent' | 'delivered' | 'read'; // Estado del mensaje
}

export interface WhatsAppConversation {
  id: string;
  name: string;
  lastMessageTime: string;
  firstMessageTime: string;
  messageCount: number;
  preview: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface WhatsAppMessage {
  timestamp: string;
  sender: string;
  text: string;
  id?: string;
  from?: string;                 // Para archivos directos
  message?: string;              // Para archivos directos
}

export interface SendMessageRequest {
  to: string;                    // Número de teléfono destino
  message: string;               // Contenido del mensaje
  conversationId?: string;       // ID de la conversación (opcional)
  mlStuff: {
    whatsAppServerPort: number;
    whatsAppApiPort: number;
    mlServerPort: number;
  };
}

export interface SendMessageResponse {
  success: boolean;
  messageId?: string;
  status?: string;
  error?: string;
  message?: string;
}

// ==================== SERVICIO PRINCIPAL ====================

export class SellerConvService {

  // ==================== MÉTODOS DE ANÁLISIS Y EXTRACCIÓN ====================

  /**
   * Extrae nombre del contacto desde ID
   */
  private static extractContactNameFromId(contactId: string): string {
    const phoneNumber = contactId.split('@')[0].replace(/^SB_/, '');
    const formattedPhone = this.formatPhoneNumber(phoneNumber);
    return `Cliente ${formattedPhone}`;
  }

  /**
   * Extrae teléfono desde ID
   */
  private static extractPhoneFromId(contactId: string): string {
    const phoneNumber = contactId.split('@')[0].replace(/^SB_/, '');
    return this.formatPhoneNumber(phoneNumber);
  }

  /**
   * Formatea número de teléfono
   */
  private static formatPhoneNumber(phoneNumber: string): string {
    const numericPhone = phoneNumber.replace(/\D/g, '');
    
    if (numericPhone.length > 10) {
      const countryCode = numericPhone.substring(0, numericPhone.length - 10);
      const localNumber = numericPhone.substring(numericPhone.length - 10);
      
      let formattedNumber = `+${countryCode} `;
      
      if (localNumber.length === 10) {
        formattedNumber += `${localNumber.substring(0, 3)}-${localNumber.substring(3, 6)}-${localNumber.substring(6)}`;
      } else {
        formattedNumber += localNumber;
      }
      
      return formattedNumber;
    }
    
    if (numericPhone.length === 10) {
      return `${numericPhone.substring(0, 3)}-${numericPhone.substring(3, 6)}-${numericPhone.substring(6)}`;
    }
    
    return phoneNumber;
  }

  /**
   * Determina si conversación está activa
   */
  private static isConversationActive(lastMessageTime: string): boolean {
    if (!lastMessageTime) return false;
    
    try {
      const currentTime = new Date().getTime();
      const lastTime = new Date(lastMessageTime).getTime();
      const fiveMinutesInMs = 5 * 60 * 1000;
      
      return (currentTime - lastTime) <= fiveMinutesInMs;
    } catch (error) {
      return false;
    }
  }

  /**
   * Determina etapa de venta desde mensajes
   */
  private static determineStageFromMessages(messages: WhatsAppMessage[]): string {
    if (!messages || messages.length === 0) return 'prospecting';
    
    try {
      const lastMessages = messages.slice(-5); // Últimos 5 mensajes
      const messageText = lastMessages.map(m => {
        const text = m.text || m.message || '';
        return text.toLowerCase();
      }).join(' ');
      
      // Palabras clave para cada etapa
      if (messageText.includes('precio') || messageText.includes('oferta') || messageText.includes('descuento')) {
        return 'negotiation';
      } else if (messageText.includes('interesa') || messageText.includes('información') || messageText.includes('detalle')) {
        return 'interest';
      } else if (messageText.includes('comprar') || messageText.includes('confirmo') || messageText.includes('acepto')) {
        return 'closing';
      } else if (messageText.includes('gracias') && messageText.includes('compra')) {
        return 'closed';
      } else if (messageText.includes('no me interesa') || messageText.includes('no gracias')) {
        return 'lost';
      }
      
      return 'prospecting'; // Default
    } catch (error) {
      return 'prospecting';
    }
  }

  /**
   * Estima valor de venta desde mensajes
   */
  private static estimateValueFromMessages(messages: WhatsAppMessage[]): number {
    if (!messages || messages.length === 0) return 25000;
    
    try {
      const messageText = messages.map(m => {
        const text = m.text || m.message || '';
        return text.toLowerCase();
      }).join(' ');
      
      // Detectar menciones de precios
      const priceMatches = messageText.match(/\$?\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g);
      if (priceMatches && priceMatches.length > 0) {
        const prices = priceMatches.map(p => parseFloat(p.replace(/[$,]/g, '')));
        return Math.max(...prices);
      }
      
      // Valores estimados por palabras clave
      if (messageText.includes('consultoría') || messageText.includes('servicio')) {
        return 150000; // CLP
      } else if (messageText.includes('producto') || messageText.includes('software')) {
        return 75000;  // CLP
      } else if (messageText.includes('curso') || messageText.includes('capacitación')) {
        return 50000;  // CLP
      }
      
      return 25000; // Valor por defecto
    } catch (error) {
      return 25000;
    }
  }

  /**
   * Calcula probabilidad de cierre desde mensajes
   */
  private static calculateProbabilityFromMessages(messages: WhatsAppMessage[]): number {
    if (!messages || messages.length === 0) return 25;
    
    try {
      const stage = this.determineStageFromMessages(messages);
      const messageCount = messages.length;
      const lastMessage = messages[messages.length - 1];
      const isActive = this.isConversationActive(lastMessage.timestamp);
      
      let baseProbability = 25; // Base
      
      // Ajuste por etapa
      switch (stage) {
        case 'prospecting': baseProbability = 15; break;
        case 'interest': baseProbability = 35; break;
        case 'negotiation': baseProbability = 65; break;
        case 'closing': baseProbability = 85; break;
        case 'closed': baseProbability = 100; break;
        case 'lost': baseProbability = 0; break;
      }
      
      // Ajuste por cantidad de mensajes (más interacción = mayor probabilidad)
      if (messageCount > 10) baseProbability += 10;
      if (messageCount > 20) baseProbability += 10;
      
      // Ajuste por actividad reciente
      if (isActive) baseProbability += 15;
      
      return Math.min(100, Math.max(0, baseProbability));
    } catch (error) {
      return 25;
    }
  }

  /**
   * Extrae perfil usado desde mensajes
   */
  private static extractProfileFromMessages(messages: WhatsAppMessage[]): string {
    if (!messages || messages.length === 0) return 'consultive';
    
    try {
      const botMessages = messages.filter(m => {
        const sender = m.sender || m.from;
        return sender === 'bot';
      });
      
      if (botMessages.length === 0) return 'consultive';

      const messageText = botMessages.map(m => {
        const text = m.text || m.message || '';
        return text.toLowerCase();
      }).join(' ');

      // Detectar personalidad por contenido
      if (messageText.includes('¡') || messageText.includes('urgente') || messageText.includes('ahora')) {
        return 'aggressive';
      } else if (messageText.includes('información') || messageText.includes('explico') || messageText.includes('detalles')) {
        return 'educational';
      } else if (messageText.includes('😊') || messageText.includes('encanta') || messageText.includes('genial')) {
        return 'friendly';
      }

      return 'consultive'; // Default
    } catch (error) {
      return 'consultive';
    }
  }

  /**
   * Extrae producto ofrecido desde mensajes
   */
  private static extractProductFromMessages(messages: WhatsAppMessage[]): string {
    if (!messages || messages.length === 0) return 'Producto/Servicio';
    
    try {
      const botMessages = messages.filter(m => {
        const sender = m.sender || m.from;
        return sender === 'bot';
      });
      
      if (botMessages.length === 0) return 'Producto/Servicio';

      const messageText = botMessages.map(m => {
        const text = m.text || m.message || '';
        return text.toLowerCase();
      }).join(' ');

      // Detectar producto por palabras clave
      if (messageText.includes('anubis') || messageText.includes('ai') || messageText.includes('inteligencia artificial')) {
        return 'ANUBIS AI Platform';
      } else if (messageText.includes('consultoría')) {
        return 'Consultoría Especializada';
      } else if (messageText.includes('curso') || messageText.includes('capacitación')) {
        return 'Curso/Capacitación';
      } else if (messageText.includes('software') || messageText.includes('sistema')) {
        return 'Software/Sistema';
      }

      return 'Producto/Servicio';
    } catch (error) {
      return 'Producto/Servicio';
    }
  }

  // ==================== MÉTODOS PARA ENDPOINT FALLBACK ====================

  /**
   * Obtiene mensajes de una conversación específica (para endpoint fallback)
   */
  static async getConversationMessages(
    conversationId: string, 
    userConfig: {
      whatsAppServerPort: number;
      whatsAppApiPort: number;
      mlServerPort: number;
    }
  ): Promise<WhatsAppMessage[]> {
    try {
      const API_TOKEN_KEY = getHatateToken();

      const response: AxiosResponse = await axios.get(`${API_BASE_URL}/conversation-detail`, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `${API_TOKEN_KEY}`,
          'Content-Type': 'application/json'
        },
        params: { 
          id: conversationId 
        },
        data: {
          mlStuff: {
            whatsAppServerPort: userConfig.whatsAppServerPort,
            whatsAppApiPort: userConfig.whatsAppApiPort,
            mlServerPort: userConfig.mlServerPort
          }
        }
      });

      if (!response.data.success) {
        console.warn(`⚠️ No se pudieron obtener mensajes para ${conversationId}`);
        return [];
      }

      const messages: WhatsAppMessage[] = response.data.messages || [];
      
      // Asegurar que cada mensaje tenga un ID único
      return messages.map((msg, index) => ({
        ...msg,
        id: msg.id || `${conversationId}-${index}-${msg.timestamp}-${msg.sender}`
      }));

    } catch (error: any) {
      console.warn(`⚠️ Error obteniendo mensajes para ${conversationId}:`, error.message);
      return [];
    }
  }

  // Métodos auxiliares para compatibilidad con endpoint fallback
  private static extractContactName(conversation: WhatsAppConversation): string {
    if (conversation.name && 
        conversation.name !== conversation.id && 
        !conversation.name.includes('@')) {
      return conversation.name;
    }
    
    if (conversation.id.includes('@g.us')) {
      const groupParts = conversation.id.split('@g.us')[0];
      const groupNamePart = groupParts.split('-').pop() || groupParts;
      return `Grupo ${groupNamePart}`;
    } else if (conversation.id.includes('@c.us')) {
      const phoneNumber = conversation.id.split('@c.us')[0].replace(/^SB_/, '');
      return this.formatPhoneNumber(phoneNumber);
    }
    
    return conversation.id.replace(/^SB_/, '');
  }

  private static extractPhoneNumber(chatId: string): string {
    if (chatId.includes('@c.us')) {
      const phoneNumber = chatId.split('@c.us')[0];
      const cleanPhone = phoneNumber.replace(/^SB_/, '');
      return this.formatPhoneNumber(cleanPhone);
    } else if (chatId.includes('@g.us')) {
      return chatId;
    }
    
    return chatId.replace(/^SB_/, '');
  }

  private static isConversationActiveOld(lastActivityTime: string): boolean {
    const currentTime = new Date().getTime();
    const lastMessageTime = new Date(lastActivityTime).getTime();
    const fiveMinutesInMs = 5 * 60 * 1000;
    
    return (currentTime - lastMessageTime) <= fiveMinutesInMs;
  }

  private static determineSellerStage(conversation: WhatsAppConversation, messages: WhatsAppMessage[]): string {
    const lastMessages = messages.slice(-5);
    const messageText = lastMessages.map(m => m.text.toLowerCase()).join(' ');
    
    if (messageText.includes('precio') || messageText.includes('oferta') || messageText.includes('descuento')) {
      return 'negotiation';
    } else if (messageText.includes('interesa') || messageText.includes('información') || messageText.includes('detalle')) {
      return 'interest';
    } else if (messageText.includes('comprar') || messageText.includes('confirmo') || messageText.includes('acepto')) {
      return 'closing';
    } else if (messageText.includes('gracias') && messageText.includes('compra')) {
      return 'closed';
    } else if (messageText.includes('no me interesa') || messageText.includes('no gracias')) {
      return 'lost';
    }
    
    return 'prospecting';
  }

  private static calculateCloseProbability(conversation: WhatsAppConversation, messages: WhatsAppMessage[]): number {
    const stage = this.determineSellerStage(conversation, messages);
    const messageCount = conversation.messageCount;
    const isActive = this.isConversationActiveOld(conversation.lastMessageTime);
    
    let baseProbability = 25; // Base
    
    // Ajuste por etapa
    switch (stage) {
      case 'prospecting': baseProbability = 15; break;
      case 'interest': baseProbability = 35; break;
      case 'negotiation': baseProbability = 65; break;
      case 'closing': baseProbability = 85; break;
      case 'closed': baseProbability = 100; break;
      case 'lost': baseProbability = 0; break;
    }
    
    // Ajuste por cantidad de mensajes
    if (messageCount > 10) baseProbability += 10;
    if (messageCount > 20) baseProbability += 10;
    
    // Ajuste por actividad reciente
    if (isActive) baseProbability += 15;
    
    return Math.min(100, Math.max(0, baseProbability));
  }

  private static estimateSaleValue(conversation: WhatsAppConversation, messages: WhatsAppMessage[]): number {
    const messageText = messages.map(m => m.text.toLowerCase()).join(' ');
    
    // Detectar mentions de precios
    const priceMatches = messageText.match(/\$?\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g);
    if (priceMatches && priceMatches.length > 0) {
      const prices = priceMatches.map(p => parseFloat(p.replace(/[$,]/g, '')));
      return Math.max(...prices);
    }
    
    // Valores estimados por palabras clave
    if (messageText.includes('consultoría') || messageText.includes('servicio')) {
      return 150000; // CLP
    } else if (messageText.includes('producto') || messageText.includes('software')) {
      return 75000;  // CLP
    } else if (messageText.includes('curso') || messageText.includes('capacitación')) {
      return 50000;  // CLP
    }
    
    return 25000; // Valor por defecto
  }

  private static extractProfileFromMessagesOld(messages: WhatsAppMessage[]): string {
    const botMessages = messages.filter(m => m.sender === 'bot');
    
    if (botMessages.length === 0) return 'consultive';

    const messageText = botMessages.map(m => m.text.toLowerCase()).join(' ');

    // Detectar personalidad por contenido
    if (messageText.includes('¡') || messageText.includes('urgente') || messageText.includes('ahora')) {
      return 'aggressive';
    } else if (messageText.includes('información') || messageText.includes('explico') || messageText.includes('detalles')) {
      return 'educational';
    } else if (messageText.includes('😊') || messageText.includes('encanta') || messageText.includes('genial')) {
      return 'friendly';
    }

    return 'consultive'; // Default
  }

  private static extractProductFromMessagesOld(messages: WhatsAppMessage[]): string {
    const botMessages = messages.filter(m => m.sender === 'bot');
    
    if (botMessages.length === 0) return 'Producto/Servicio';

    const messageText = botMessages.map(m => m.text.toLowerCase()).join(' ');

    // Detectar producto por palabras clave
    if (messageText.includes('anubis') || messageText.includes('ai') || messageText.includes('inteligencia artificial')) {
      return 'ANUBIS AI Platform';
    } else if (messageText.includes('consultoría')) {
      return 'Consultoría Especializada';
    } else if (messageText.includes('curso') || messageText.includes('capacitación')) {
      return 'Curso/Capacitación';
    } else if (messageText.includes('software') || messageText.includes('sistema')) {
      return 'Software/Sistema';
    }

    return 'Producto/Servicio';
  }

  // ==================== MÉTODOS DE ENVÍO DE MENSAJES ====================

  /**
   * Envía un mensaje a través del SellerBot
   */
  static async sendSellerBotMessage(
    request: SendMessageRequest
  ): Promise<SendMessageResponse> {
    try {
      const API_TOKEN_KEY = getHatateToken();

      console.log('📤 Enviando mensaje del SellerBot:', {
        to: request.to,
        messageLength: request.message.length
      });

      const response: AxiosResponse = await axios.post(API_SEND_URL, {
        to: request.to,
        message: `🤖 ${request.message}`, // Prefijo para identificar mensajes del bot
        mlStuff: request.mlStuff
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          'Authorization': `${API_TOKEN_KEY}`
        }
      });

      if (response.data.success) {
        console.log('✅ Mensaje del SellerBot enviado exitosamente');
        return {
          success: true,
          messageId: response.data.messageId,
          status: response.data.status || 'sent'
        };
      } else {
        console.error('❌ Error enviando mensaje del SellerBot:', response.data);
        return {
          success: false,
          error: response.data.message || 'Error desconocido'
        };
      }

    } catch (error: any) {
      console.error('❌ Error en envío de mensaje del SellerBot:', error);
      return {
        success: false,
        error: `Error de conexión: ${error.message}`
      };
    }
  }

  /**
   * Alias para sendSellerBotMessage - Para compatibilidad con ConversationNetwork
   */
  static async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    return this.sendSellerBotMessage(request);
  }

  // ==================== MÉTODOS DE GESTIÓN ====================

  /**
   * Actualiza el estado de una conversación del SellerBot
   */
  static async updateConversationStage(
    conversationId: string,
    newStage: string,
    userConfig: {
      whatsAppServerPort: number;
      whatsAppApiPort: number;
      mlServerPort: number;
    }
  ): Promise<boolean> {
    try {
      // Esta funcionalidad se podría implementar si el SellerBot backend 
      // tiene un endpoint para actualizar el estado de conversaciones
      console.log(`📝 Actualizando conversación ${conversationId} a etapa: ${newStage}`);
      
      // Por ahora solo logueamos, pero aquí iría la llamada al endpoint
      // del SellerBot para actualizar el estado
      
      return true;
    } catch (error) {
      console.error('❌ Error actualizando estado de conversación:', error);
      return false;
    }
  }

/**
 * Obtiene conversaciones del SellerBot - NUEVA IMPLEMENTACIÓN
 * 1. Obtiene TODAS las conversaciones (una sola consulta)
 * 2. Obtiene contactos de la BD
 * 3. Filtra conversaciones que coincidan con números de la BD
 */
static async getSellerBotConversations(userConfig: {
  whatsAppServerPort: number;
  whatsAppApiPort: number;
  mlServerPort: number;
}): Promise<SellerConversation[]> {
  try {
    console.log('🔍 Obteniendo conversaciones del SellerBot...');
    
    // 1. Obtener TODAS las conversaciones (una sola consulta)
    const allConversations = await this.fetchAllConversations(userConfig);
    
    // 2. Obtener contactos de la BD
    const contactsResponse = await SellerbotService.getContacts({
      status: 'all',
      limit: 1000
    });
    
    if (!contactsResponse || contactsResponse.success === false) {
      console.log('⚠️ No se pudieron obtener contactos de BD, mostrando todas las conversaciones');
      return this.convertToSellerConversations(allConversations);
    }
    
    const contacts = contactsResponse.data?.contacts || [];
    console.log(`📊 Contactos en BD: ${contacts.length}`);
    
    if (contacts.length === 0) {
      console.log('📋 No hay contactos en BD');
      return [];
    }
    
    // 3. Filtrar conversaciones que coincidan con números de la BD
    const filteredConversations = this.filterConversationsByContacts(allConversations, contacts);
    
    console.log(`✅ Conversaciones filtradas: ${filteredConversations.length} de ${allConversations.length} totales`);
    return this.convertToSellerConversations(filteredConversations);
    
  } catch (error: any) {
    console.error('❌ Error:', error);
    return [];
  }
}

/**
 * Obtiene TODAS las conversaciones disponibles (una sola consulta)
 */
private static async fetchAllConversations(userConfig: {
  whatsAppServerPort: number;
  whatsAppApiPort: number;
  mlServerPort: number;
}): Promise<any[]> {
  try {
    const API_TOKEN_KEY = getHatateToken();
    const timestamp = new Date().toISOString();

    console.log('📡 Consultando endpoint:', `${API_BASE_URL}/conversations`);
    
    const response = await axios.get(`${API_BASE_URL}/conversations`, {
      headers: { 
        'x-api-key': API_KEY,
        'Authorization': `${API_TOKEN_KEY}`
      },
      data: {
        prompt: [{ 
          content: '',
          role: 'user',
          time: timestamp
        }],
        chatId: `whatsapp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        skill: 'general_v1',
        module: 'general_general',
        mlStuff: userConfig
      },
      timeout: 10000
    });

    if (response.data.success) {
      const conversations = response.data.conversations || [];
      console.log(`✅ Obtenidas ${conversations.length} conversaciones totales`);
      return conversations;
    } else {
      console.warn('⚠️ Error en respuesta:', response.data.message);
      return [];
    }
  } catch (error) {
    console.error('❌ Error obteniendo conversaciones:', error);
    return [];
  }
}

/**
 * Filtra conversaciones que coincidan con contactos de la BD
 */
private static filterConversationsByContacts(conversations: any[], contacts: any[]): any[] {
  // Extraer números de teléfono de los contactos de la BD
  const contactPhones = contacts.map(contact => {
    const cleanPhone = contact.phone.replace(/\D/g, '');
    return cleanPhone;
  });
  
  console.log('📞 Números en BD:', contactPhones);
  
// Filtrar conversaciones que coincidan
const filtered = conversations.filter(conv => {
  // EXCLUIR grupos (@g.us) completamente
  if (conv.id.includes('@g.us')) {
    console.log(`❌ Grupo excluido: ${conv.id}`);
    return false;
  }
  
  // Extraer número de teléfono del ID de conversación
  const conversationPhone = this.extractPhoneFromConversationId(conv.id);
  
  // Verificar coincidencia EXACTA para evitar falsos positivos
  const matches = contactPhones.some(dbPhone => {
    return conversationPhone === dbPhone;
  });
  
  if (matches) {
    console.log(`✅ Coincidencia: ${conv.id} -> ${conversationPhone}`);
  }
  
  return matches;
});
  
  return filtered;
}

/**
 * Extrae número de teléfono desde ID de conversación
 */
private static extractPhoneFromConversationId(conversationId: string): string {
  // Manejar diferentes formatos:
  // - 56982286397@s.whatsapp.net
  // - SB_56982286397@c.us
  // - 56982286397@c.us
  
  let phoneNumber = '';
  
  if (conversationId.includes('@s.whatsapp.net')) {
    phoneNumber = conversationId.split('@s.whatsapp.net')[0];
  } else if (conversationId.includes('@c.us')) {
    phoneNumber = conversationId.split('@c.us')[0];
  } else {
    phoneNumber = conversationId;
  }
  
  // Remover prefijo SB_ si existe
  phoneNumber = phoneNumber.replace(/^SB_/, '');
  
  // Solo números
  return phoneNumber.replace(/\D/g, '');
}

/**
 * Convierte conversaciones raw a formato SellerConversation
 */
private static convertToSellerConversations(conversations: any[]): SellerConversation[] {
  return conversations.map(conv => {
    const phoneNumber = this.extractPhoneFromConversationId(conv.id);
    const chatId = `SB_${phoneNumber}@c.us`;
    
    return {
      id: chatId,
      contactName: this.formatContactName(conv),
      contactPhone: this.formatPhoneNumber(phoneNumber),
      contactEmail: '', // No disponible en este endpoint
      lastMessageTime: conv.lastMessageTime || new Date().toISOString(),
      firstMessageTime: conv.firstMessageTime || new Date().toISOString(),
      messageCount: conv.messageCount || 0,
      preview: conv.preview || 'Sin vista previa',
      isActive: this.isConversationActive(conv.lastMessageTime || ''),
      stage: 'prospecting', // Valor por defecto
      value: 25000, // Valor por defecto
      probability: 50, // Valor por defecto
      profileUsed: 'consultive',
      productOffered: 'Producto/Servicio',
      whatsappChatId: conv.id, // ID original
      messages: [], // Se cargan bajo demanda
      source: 'whatsapp_filtered_data',
      notes: `Conversación filtrada: ${this.formatContactName(conv)}`
    };
  });
}

/**
 * Formatea nombre de contacto
 */
private static formatContactName(conversation: any): string {
  if (conversation.name && 
      conversation.name !== conversation.id && 
      !conversation.name.includes('@')) {
    return conversation.name;
  }
  
  const phoneNumber = this.extractPhoneFromConversationId(conversation.id);
  return this.formatPhoneNumber(phoneNumber);
}

  /**
   * Obtiene estadísticas rápidas de las conversaciones del SellerBot
   */
  static async getSellerBotStats(userConfig: {
    whatsAppServerPort: number;
    whatsAppApiPort: number;
    mlServerPort: number;
  }): Promise<{
    totalConversations: number;
    activeConversations: number;
    totalValue: number;
    averageProbability: number;
    stageDistribution: { [key: string]: number };
  }> {
    try {
      const conversations = await this.getSellerBotConversations(userConfig);
      
      const totalConversations = conversations.length;
      const activeConversations = conversations.filter(c => c.isActive).length;
      const totalValue = conversations.reduce((sum, c) => sum + c.value, 0);
      const averageProbability = conversations.length > 0 
        ? conversations.reduce((sum, c) => sum + c.probability, 0) / conversations.length 
        : 0;
      
      const stageDistribution: { [key: string]: number } = {};
      conversations.forEach(c => {
        stageDistribution[c.stage] = (stageDistribution[c.stage] || 0) + 1;
      });

      return {
        totalConversations,
        activeConversations,
        totalValue,
        averageProbability,
        stageDistribution
      };

    } catch (error) {
      console.error('❌ Error obteniendo estadísticas del SellerBot:', error);
      return {
        totalConversations: 0,
        activeConversations: 0,
        totalValue: 0,
        averageProbability: 0,
        stageDistribution: {}
      };
    }
  }
}

// ==================== EXPORTS ADICIONALES ====================

/**
 * Export de funciones de conveniencia para uso directo
 */
export const sendMessage = (request: SendMessageRequest) => SellerConvService.sendMessage(request);
export const getConversations = (userConfig: any) => SellerConvService.getSellerBotConversations(userConfig);
export const getStats = (userConfig: any) => SellerConvService.getSellerBotStats(userConfig);

// ==================== UTILIDADES DE FORMATEO EXPORTADAS ====================

/**
 * Extrae y formatea el número de teléfono desde un WhatsApp Chat ID
 */
export const extractPhoneNumber = (chatId: string): string => {
  if (chatId.includes('@c.us')) {
    const phoneNumber = chatId.split('@c.us')[0];
    // Remover prefijo SB_ si existe
    const cleanPhone = phoneNumber.replace(/^SB_/, '');
    return formatPhoneNumber(cleanPhone);
  } else if (chatId.includes('@g.us')) {
    // Manejar grupos si es necesario
    return chatId;
  }
  
  return chatId.replace(/^SB_/, '');
};

/**
 * Formatea un número de teléfono para mostrar
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  const numericPhone = phoneNumber.replace(/\D/g, '');
  
  if (numericPhone.length > 10) {
    const countryCode = numericPhone.substring(0, numericPhone.length - 10);
    const localNumber = numericPhone.substring(numericPhone.length - 10);
    
    let formattedNumber = `+${countryCode} `;
    
    if (localNumber.length === 10) {
      formattedNumber += `${localNumber.substring(0, 3)}-${localNumber.substring(3, 6)}-${localNumber.substring(6)}`;
    } else {
      formattedNumber += localNumber;
    }
    
    return formattedNumber;
  }
  
  if (numericPhone.length === 10) {
    return `${numericPhone.substring(0, 3)}-${numericPhone.substring(3, 6)}-${numericPhone.substring(6)}`;
  }
  
  return phoneNumber;
};

/**
 * Extrae el nombre del contacto desde una conversación
 */
export const extractContactName = (conversation: WhatsAppConversation): string => {
  if (conversation.name && 
      conversation.name !== conversation.id && 
      !conversation.name.includes('@')) {
    return conversation.name;
  }
  
  if (conversation.id.includes('@g.us')) {
    const groupParts = conversation.id.split('@g.us')[0];
    const groupNamePart = groupParts.split('-').pop() || groupParts;
    return `Grupo ${groupNamePart}`;
  } else if (conversation.id.includes('@c.us')) {
    const phoneNumber = conversation.id.split('@c.us')[0].replace(/^SB_/, '');
    return formatPhoneNumber(phoneNumber);
  }
  
  return conversation.id.replace(/^SB_/, '');
};

/**
 * Determina si una conversación está activa (última actividad < 5 minutos)
 */
export const isConversationActive = (lastActivityTime: string): boolean => {
  const currentTime = new Date().getTime();
  const lastMessageTime = new Date(lastActivityTime).getTime();
  const fiveMinutesInMs = 5 * 60 * 1000;
  
  return (currentTime - lastMessageTime) <= fiveMinutesInMs;
};

/**
 * Determina la etapa de venta basada en el análisis de mensajes
 */
export const determineSellerStage = (conversation: WhatsAppConversation, messages: WhatsAppMessage[]): string => {
  // Lógica básica para determinar etapa basada en contenido de mensajes
  const lastMessages = messages.slice(-5); // Últimos 5 mensajes
  const messageText = lastMessages.map(m => m.text.toLowerCase()).join(' ');
  
  // Palabras clave para cada etapa
  if (messageText.includes('precio') || messageText.includes('oferta') || messageText.includes('descuento')) {
    return 'negotiation';
  } else if (messageText.includes('interesa') || messageText.includes('información') || messageText.includes('detalle')) {
    return 'interest';
  } else if (messageText.includes('comprar') || messageText.includes('confirmo') || messageText.includes('acepto')) {
    return 'closing';
  } else if (messageText.includes('gracias') && messageText.includes('compra')) {
    return 'closed';
  } else if (messageText.includes('no me interesa') || messageText.includes('no gracias')) {
    return 'lost';
  }
  
  return 'prospecting'; // Default
};

/**
 * Calcula la probabilidad de cierre basada en la interacción
 */
export const calculateCloseProbability = (conversation: WhatsAppConversation, messages: WhatsAppMessage[]): number => {
  const stage = determineSellerStage(conversation, messages);
  const messageCount = conversation.messageCount;
  const isActive = isConversationActive(conversation.lastMessageTime);
  
  let baseProbability = 25; // Base
  
  // Ajuste por etapa
  switch (stage) {
    case 'prospecting': baseProbability = 15; break;
    case 'interest': baseProbability = 35; break;
    case 'negotiation': baseProbability = 65; break;
    case 'closing': baseProbability = 85; break;
    case 'closed': baseProbability = 100; break;
    case 'lost': baseProbability = 0; break;
  }
  
  // Ajuste por cantidad de mensajes (más interacción = mayor probabilidad)
  if (messageCount > 10) baseProbability += 10;
  if (messageCount > 20) baseProbability += 10;
  
  // Ajuste por actividad reciente
  if (isActive) baseProbability += 15;
  
  return Math.min(100, Math.max(0, baseProbability));
};

/**
 * Estima el valor de la venta basado en el producto y la conversación
 */
export const estimateSaleValue = (conversation: WhatsAppConversation, messages: WhatsAppMessage[]): number => {
  // Valores base por tipo de producto/servicio detectado
  const messageText = messages.map(m => m.text.toLowerCase()).join(' ');
  
  // Detectar mentions de precios
  const priceMatches = messageText.match(/\$?\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g);
  if (priceMatches && priceMatches.length > 0) {
    const prices = priceMatches.map(p => parseFloat(p.replace(/[$,]/g, '')));
    return Math.max(...prices);
  }
  
  // Valores estimados por palabras clave
  if (messageText.includes('consultoría') || messageText.includes('servicio')) {
    return 150000; // CLP
  } else if (messageText.includes('producto') || messageText.includes('software')) {
    return 75000;  // CLP
  } else if (messageText.includes('curso') || messageText.includes('capacitación')) {
    return 50000;  // CLP
  }
  
  return 25000; // Valor por defecto
};

// Export por defecto del servicio principal
export default SellerConvService;

// ==================== INFORMACIÓN DE USO ====================

/*
🎯 ESTRATEGIAS IMPLEMENTADAS:

1️⃣ LECTURA DIRECTA DE ARCHIVOS (Principal)
   - Lee ../WS/database/conversations/SB_*.json
   - Sin dependencia de red
   - Procesamiento local más rápido
   - Manejo robusto de errores

2️⃣ ENDPOINT CON MLSTUFF (Fallback)
   - Usa https://backend.anubisai.net/api/whatsapp/bot/conversations
   - Validaciones mlStuff completas
   - Filtrado por prefijo SB_
   - Datos en tiempo real

✅ FUNCIONALIDADES:
- Lectura directa de archivos JSON
- Análisis inteligente de etapas de venta
- Cálculo de probabilidades de cierre
- Estimación de valores de venta
- Extracción de perfiles y productos
- Envío de mensajes con prefijo 🤖
- Estadísticas en tiempo real
- Limpieza de archivos corruptos
- Validación de directorios

🔧 CONFIGURACIÓN:
- Ruta: ../WS/database/conversations/
- Filtro: SB_*.json
- Endpoint: https://backend.anubisai.net/api/whatsapp/bot/conversations
- Fallback automático sin dependencias

📊 LOGGING DETALLADO:
- Estado de lectura de archivos
- Número de archivos procesados
- Errores específicos por archivo
- Estadísticas de procesamiento
- Información de fallback

🚀 RENDIMIENTO:
- Lectura directa = Más rápido
- Sin llamadas de red innecesarias
- Procesamiento local optimizado
- Fallback robusto garantizado
*/